/*
 * Date: 2025
 * Description: Entry point of the application.
 * Author: Philippe Leroux @ skitsc
 */


//Modules
import { useMemo , useEffect , useContext , useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { CssBaseline , ThemeProvider } from '@mui/material';

//Pages
import Catalog from "./pages/catalog";
import TopBar from './components/ui/top.bar';
import Logs from './pages/logs';
import Lists from "./pages/lists";
import Bot from "./pages/bot";
import Splash from "./pages/splash";
import Login from "./pages/login";
import Logout from "./pages/logout";
import Landing from "./pages/landing";
import Preferences from "./pages/settings/preference";
import Users from "./pages/settings/user";
import MainSettings from "./pages/settings/main.settings";
import MyAccount from "./pages/account";
import Message from "./pages/message";

//Contexts
import { SocketProvider , SocketContext, useSocketEvent, socket_id  } from './context/socket.context';
import ThemeHook from './context/theme.context';
import mainHook from "./context/main.context";
import { MainContext, ThemeContext } from "./context/context";
import { generateTheme } from "./theme";

//Styles
import "./index.css";

//Components
import LoadingFull from "./components/utils/loading.full";

//Constants && functions
import { handleFetch, handleLogout } from "./api/fetch";

//Interfaces
import { i_initial_props, i_promise, i_socket_response } from "./interfaces/utility.interface";
import Orders from "./pages/order";
import { AlertProvider } from "./context/alert.context";
import { ShowModalProvider } from "./context/view.modal.context";
import { FitModalProvider } from "./context/fit.modal.context";
import TowerGame from "./pages/sm.game";

const App = () => {
    const [ isLoading , setIsLoading ] = useState<boolean>(true);
    const [ mobile ] = useState<boolean>(false);
    //Contexts
    const ThemeState = ThemeHook();
    const ContextState = mainHook();
    const socket = useContext(SocketContext);
	const prePath = process.env.REACT_APP_PRE_PATH ? process.env.REACT_APP_PRE_PATH : '';
    const themeWithLocale = useMemo( () =>
			generateTheme( ThemeState.mode, ThemeState.primary, ThemeState.secondary, ThemeState.mode === "Dark" ? "#222" : "#FFF",
                 ThemeState.mode === "Dark" ? "#555" : "#111", ThemeState.mode === "Dark" ? "#FFF" : "#000", ThemeState.selected, ThemeState.hover ),
	    [ ThemeState.mode, ThemeState.primary, ThemeState.secondary, ThemeState.hover, ThemeState.selected ]
	);
	useEffect(() => {
		if (!socket.connected && ContextState.authenticated) {
			socket.connect();
			const checkJwt = () => {
				socket.emit("check-jwt");
			};
			const checkConnection = async () : Promise<boolean> => {
				const res = await handleFetch('/validate-cookie', 'GET', true, null);
				if(res.type === 'Success'){  
					return true;
				} else { 
					return false
				}
			};
			const handleConnect = () => {
				const socketInterval = setInterval(checkJwt, 10000);
				socket.on("logout", async() => {
					const isConnected = await checkConnection();
					if (isConnected) {
						socket.connect();
						socket.emit('join', { roomId : ContextState.user.type , user : ContextState.user , socket_id : socket_id});
					} else {
						clearInterval(socketInterval);
						socket.disconnect()
						ContextState.setCurrentAuth(false)
						await handleLogout()
					}
				});
			};

			socket.once("connect", handleConnect);

			return () => {
				socket.off("connect", handleConnect);
			};
		}
	}, [ContextState.authenticated , socket]);
	useSocketEvent('connected', (output : i_socket_response ) => {
		socket.emit('join', { roomId: ContextState.user.type === 'admin' ? 'admin' : ContextState.user._id , user_id:  ContextState.user._id, socket_id: socket.id,  });
	})
		
    useEffect(() => {
		const f_fetch_handler = async () : Promise<void> => {
			setIsLoading(true);
			const api_rep: i_promise = await handleFetch("/auth", "GET", true, null);
			setIsLoading(false);
			if(api_rep.type === "Success") {
				ContextState.HandleLogin(api_rep.data);
				return
			}
            if(api_rep.type !== "Unauthorized") {
				alert("Theres an issue with the server, contact a wizard")
			}
		};
		f_fetch_handler();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	},[]);

    const useAuth = () => {
		if (ContextState.authenticated) {
			return true;
		} else return false;
	};

    const PrivateRoute = ({ children }: any) => {
		const auth = useAuth();
		return auth ? children : <Navigate to={"/login"} />;
	};
	const PublicRoutes = ({ children }: any) => {
		const auth = useAuth();
		return !auth ? children : <Navigate to={"/landing"} />;
	};
	const AdminRoute = ({ children }: any) => {
		const auth = useAuth();
		return auth && ContextState.user.type === "admin" ? children : <Navigate to={"/landing"} />;
	}
	const UserRoute = ({ children }: any) => {
        const auth = useAuth();
        return auth && ContextState.user.type === "user" ? children : <Navigate to={"/landing"} />;
    }
	const NeutralRoute = ({ children }: any) => {
		return children
	}
	const UPLOAD_PATH = process.env.REACT_APP_API_URL ?  process.env.REACT_APP_API_URL : ''
    const PRE_PATH = process.env.REACT_APP_PREPATH ? process.env.REACT_APP_PREPATH : ''
	
    const props : i_initial_props = {
        mobile : mobile,
		path : PRE_PATH ? PRE_PATH : "",
		uploadPath : UPLOAD_PATH ? UPLOAD_PATH : '',
		prePath : prePath? prePath : "",
    }
    return (            
        <MainContext.Provider value={ContextState}>
            <SocketProvider>
				<FitModalProvider {...props}>
				<ShowModalProvider {...props}>
					<AlertProvider {...props}>
						<ThemeContext.Provider value={ThemeState}>
							<ThemeProvider theme={themeWithLocale}>
							<CssBaseline/>
							{isLoading ? (
									<LoadingFull text={"Validating authentification ... "}/>
								) : (
								<Routes>
									<Route path={'/'} element={<NeutralRoute><Splash {...props}/></NeutralRoute>} />
									<Route path={"/login"} element={<PublicRoutes><Login {...props}/></PublicRoutes>} />
									<Route path={"/landing"} element={<PrivateRoute><TopBar {...props} component={<Landing />}></TopBar></PrivateRoute>} />
									<Route path={"/account"} element={<PrivateRoute><TopBar {...props}component={<MyAccount {...props} />}></TopBar></PrivateRoute>} />
									<Route path={"/catalog"} element={<PrivateRoute><TopBar {...props}component={<Catalog {...props} list={null} scrollToTop={() => {}}/>}></TopBar></PrivateRoute>} />
									<Route path={"/orders"} element={<AdminRoute><TopBar {...props} component={<Orders {...props} />} /></AdminRoute>} />
									<Route path={"/bot"} element={<AdminRoute><TopBar {...props} component={<Bot />}></TopBar></AdminRoute>} />
									<Route path={"/lists"} element={<UserRoute><TopBar {...props} component={<Lists {...props}/>}></TopBar></UserRoute>} />
									<Route path={"/logout"} element={<PrivateRoute><Logout /></PrivateRoute>} />
									<Route path={"/message-center"} element={<PrivateRoute><TopBar {...props} component={<Message />}></TopBar></PrivateRoute>} />
									<Route path={"/settings/preferences"} element={<AdminRoute><TopBar {...props} component={<Preferences />}></TopBar></AdminRoute>} />
									<Route path={"/settings/users"} element={<AdminRoute><TopBar {...props} component={<Users {...props} />}></TopBar></AdminRoute>} />
									<Route path={"/settings/logs"} element={<AdminRoute><TopBar {...props} component={<Logs />}></TopBar></AdminRoute>} />
									<Route path={"/settings/main-settings"} element={<AdminRoute><TopBar {...props} component={<MainSettings {...props} />}></TopBar></AdminRoute>} />
									<Route path={"/game"} element={<AdminRoute><TopBar {...props} component={<TowerGame />}></TopBar></AdminRoute>} />
									<Route path="*" element={<NeutralRoute><Navigate to={'/'} /></NeutralRoute>} />
								</Routes>
								)}
							</ThemeProvider>
						</ThemeContext.Provider>
					</AlertProvider>
				</ShowModalProvider>
				</FitModalProvider>
            </SocketProvider>
        </MainContext.Provider>
    );
}

export default App;
